import { UploadType } from 'further-types/files';
import { FileType } from '../utils/files';

export type FileUploadTypeConfig = {
  validFileTypes: Array<FileType>;
  maxSizeInBytes: number;
  /** S3 path prefix for this upload type; used for path validation (e.g. profilePic). */
  path?: string;
};

export const fileUploadTypeConfig: Record<UploadType, FileUploadTypeConfig> = {
  [UploadType.CompanyLogos]: {
    validFileTypes: [FileType.JPGImage, FileType.PNGImage, FileType.SVGImage],
    maxSizeInBytes: 5_000_000,
  },
  [UploadType.UserImages]: {
    validFileTypes: [FileType.JPGImage, FileType.PNGImage],
    maxSizeInBytes: 5_000_000,
    path: 'public/user-images',
  },
  [UploadType.FirmLogos]: {
    validFileTypes: [FileType.JPGImage, FileType.PNGImage, FileType.SVGImage],
    maxSizeInBytes: 500_000,
  },
  [UploadType.FundDocuments]: {
    validFileTypes: [
      FileType.DOCX,
      FileType.PDF,
      FileType.JPGImage,
      FileType.PNGImage,
    ],
    maxSizeInBytes: 80_000_000,
  },
  [UploadType.InterestPayments]: {
    validFileTypes: [FileType.Excel, FileType.ExcelAlt],
    maxSizeInBytes: 10_000_000,
  },
  [UploadType.ReportingAttachments]: {
    validFileTypes: [
      FileType.PDF,
      FileType.PNGImage,
      FileType.Excel,
      FileType.ExcelAlt,
    ],
    maxSizeInBytes: 20_000_000,
  },
  [UploadType.FirmDocuments]: {
    validFileTypes: [FileType.PDF],
    maxSizeInBytes: 2_000_000,
  },
  [UploadType.WithdrawalAttachments]: {
    validFileTypes: [FileType.PDF, FileType.JPGImage, FileType.PNGImage],
    maxSizeInBytes: 1_000_000,
  },
  [UploadType.InvestorIdvCertificates]: {
    validFileTypes: [
      FileType.PDF,
      FileType.DOCX,
      FileType.PNGImage,
      FileType.JPGImage,
    ],
    maxSizeInBytes: 20_000_000,
  },
  [UploadType.EisCertificates]: {
    validFileTypes: [FileType.PDF],
    maxSizeInBytes: 1_000_000,
  },
  [UploadType.PdfUpdates]: {
    validFileTypes: [FileType.PDF],
    maxSizeInBytes: 20_000_000,
  },
  [UploadType.PdfExitStatement]: {
    validFileTypes: [FileType.PDF],
    maxSizeInBytes: 2_000_000,
  },
  [UploadType.KycDocuments]: {
    validFileTypes: [
      FileType.PDF,
      FileType.JPGImage,
      FileType.PNGImage,
      FileType.DOCX,
    ],
    maxSizeInBytes: 20_000_000,
  },
  [UploadType.CustomUpdateAttachments]: {
    validFileTypes: [FileType.PDF, FileType.Excel, FileType.ExcelAlt],
    maxSizeInBytes: 10_000_000,
  },
  [UploadType.BackgroundExports]: {
    validFileTypes: [FileType.Excel, FileType.ExcelAlt, FileType.Zip],
    maxSizeInBytes: 80_000_000,
  },
  [UploadType.LpTransactionAttachments]: {
    validFileTypes: [FileType.PDF],
    maxSizeInBytes: 1_000_000,
  },
  [UploadType.LpBankStatements]: {
    validFileTypes: [FileType.CSV, FileType.Excel, FileType.ExcelAlt],
    maxSizeInBytes: 1_000_000,
  },
  [UploadType.InterestPaymentsWriteOutput]: {
    validFileTypes: [FileType.JSON],
    maxSizeInBytes: 500_000,
  },
  [UploadType.DeploymentUpdateAttachments]: {
    validFileTypes: [...Object.values(FileType)], // all file types
    maxSizeInBytes: 10_000_000,
  },
  [UploadType.DataRoomDocuments]: {
    validFileTypes: [FileType.PDF, FileType.JPGImage, FileType.PNGImage],
    maxSizeInBytes: 20_000_000,
  },
  [UploadType.InvestorClassificationDocuments]: {
    validFileTypes: [
      FileType.PDF,
      FileType.DOCX,
      FileType.Excel,
      FileType.ExcelAlt,
    ],
    maxSizeInBytes: 20_000_000,
  },
  [UploadType.InvestorTaxDocuments]: {
    validFileTypes: [
      FileType.PDF,
      FileType.JPGImage,
      FileType.PNGImage,
      FileType.DOCX,
      FileType.DOC,
    ],
    maxSizeInBytes: 20_000_000,
  },
  [UploadType.VehicleSigningDocuments]: {
    validFileTypes: [FileType.PDF, FileType.DOCX],
    maxSizeInBytes: 20_000_000,
  },
  [UploadType.OnboardingCustomRequestDocuments]: {
    validFileTypes: [
      FileType.PDF,
      FileType.DOCX,
      FileType.Excel,
      FileType.ExcelAlt,
    ],
    maxSizeInBytes: 20_000_000,
  },
  [UploadType.CommitmentDocuments]: {
    validFileTypes: [
      FileType.PDF,
      FileType.DOCX,
      FileType.Excel,
      FileType.ExcelAlt,
    ],
    maxSizeInBytes: 20_000_000,
  },
  [UploadType.CapitalActionNotices]: {
    validFileTypes: [FileType.PDF],
    maxSizeInBytes: 500_000,
  },
} as const;

export const getValidFileTypesForUploadType = (uploadType: UploadType) => {
  return fileUploadTypeConfig[uploadType].validFileTypes;
};

export const isValidS3KeyForUploadType = (
  s3Key: string,
  uploadType: UploadType,
) => {
  const path = fileUploadTypeConfig[uploadType].path;

  return path ? s3Key.startsWith(`${path}/`) : true;
};

export const getMaxSizeForUploadType = (
  uploadType: UploadType,
  unit: 'mb' | 'kb',
) => {
  const value = fileUploadTypeConfig[uploadType].maxSizeInBytes;
  return unit === 'kb' ? value / 1000 : value / 1_000_000;
};
